import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  UPDATE_VENUE_FAQS_ENDPOINT,
  UpdateVenueFaqsPayload,
  UpdateVenueFaqsResponse,
} from 'constants/payloads/venues/faqs';
import { useApiGateway } from 'hooks/useApi';
import CloseIcon from 'svg/CloseIcon';
import PlusPlain from 'svg/PlusPlain';
import Trash from 'svg/Trash';
import { Button, ButtonText } from 'components/Button';
import InputField from 'components/coaches-builder/InputField';
import { InputType } from 'components/coaches-builder/InputField/InputField';
import Modal from 'components/modals/Modal';
import type { VenueFaq } from '../../types';
import {
  EMPTY_FAQ,
  FaqFormValues,
  MAX_FAQS,
  faqFormSchema,
  flattenFaqErrors,
  toFaqFormValues,
} from './faqForm';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  initialFaqs: VenueFaq[];
  onSaved: () => void;
}

export default function EditFaqModal({ isOpen, handleClose, initialFaqs, onSaved }: Props) {
  const { post, isLoading } = useApiGateway<UpdateVenueFaqsPayload, UpdateVenueFaqsResponse>(
    UPDATE_VENUE_FAQS_ENDPOINT,
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FaqFormValues>({
    resolver: zodResolver(faqFormSchema),
    defaultValues: toFaqFormValues(initialFaqs),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'faqs' });

  // Re-seed the form each time the modal opens so it reflects the latest saved FAQs.
  useEffect(() => {
    if (isOpen) {
      reset(toFaqFormValues(initialFaqs));
    }
  }, [isOpen, initialFaqs, reset]);

  const flatErrors = flattenFaqErrors(errors);

  const onSubmit = async (data: FaqFormValues) => {
    try {
      await post({
        payload: {
          faqs: data.faqs.map((faq) => ({
            question: faq.question.trim(),
            answer: faq.answer.trim(),
          })),
        },
      });
      toast.success('FAQ updated');
      onSaved();
      handleClose();
    } catch (error) {
      toast.error('Failed to save FAQ. Please try again.');
      console.error('Error saving club FAQ:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="sm:max-w-4xl">
      <form onSubmit={handleSubmit(onSubmit)} className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-color-border-card-lightmode px-10 py-5 dark:border-color-border-card-darkmode max-sm:px-ds-lg">
          <h2 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            FAQ
          </h2>
          <ButtonText type="button" onClick={handleClose} aria-label="Close">
            <CloseIcon className="h-6 w-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:h-3.5 max-sm:w-3.5" />
          </ButtonText>
        </div>

        {/* Content */}
        <div className="flex max-h-[60vh] flex-1 flex-col gap-8 overflow-y-auto px-10 py-6 max-sm:px-ds-lg">
          {fields.map((field, index) => (
            <div key={field.id} className="flex flex-col gap-4 sm:flex-row sm:gap-6">
              <p className="typography-product-subheading shrink-0 pt-2.5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary sm:w-64">
                {`Question #${index + 1}`}
              </p>

              <div className="flex flex-1 flex-col gap-3">
                <InputField
                  name={`faqs.${index}.question`}
                  register={register}
                  errors={flatErrors}
                  placeholder="Question"
                  inputType={InputType.Regular}
                  errorBorder
                />
                <InputField
                  name={`faqs.${index}.answer`}
                  register={register}
                  errors={flatErrors}
                  placeholder="Answer"
                  inputType={InputType.Textarea}
                  errorBorder
                />

                <div className="flex items-center gap-6">
                  {index === fields.length - 1 && fields.length < MAX_FAQS && (
                    <ButtonText
                      type="button"
                      onClick={() => append(EMPTY_FAQ)}
                      className="flex items-center gap-1.5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
                    >
                      <PlusPlain className="h-4 w-4" />
                      Add question
                    </ButtonText>
                  )}
                  <ButtonText
                    type="button"
                    onClick={() => remove(index)}
                    className="flex items-center gap-1.5 text-color-error"
                  >
                    <Trash className="h-4 w-4" />
                    Delete
                  </ButtonText>
                </div>
              </div>
            </div>
          ))}

          {fields.length === 0 && (
            <ButtonText
              type="button"
              onClick={() => append(EMPTY_FAQ)}
              className="flex items-center gap-1.5 self-start text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              <PlusPlain className="h-4 w-4" />
              Add question
            </ButtonText>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-color-border-card-lightmode px-10 py-5 dark:border-color-border-card-darkmode max-sm:px-ds-lg">
          <Button type="button" variant="secondary" size="lg" isInline onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="lg" isInline loading={isLoading}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
